import {
  RegionalHighlightListingMatchAction,
  RegionalHighlightListingMatchReason,
  type RegionalHighlightSlot,
  type RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type RegionalHighlightBookingState =
  | { kind: 'hidden' }
  | { kind: 'no-usable-slot' }
  | { kind: 'assignable'; slot: RegionalHighlightSlot }
  | { kind: 'blocked'; slot: RegionalHighlightSlot }
  | { kind: 'active'; slot: RegionalHighlightSlot };

const isActiveForListing = (slot: RegionalHighlightSlot, listingId: string): boolean =>
  slot.activeBooking?.listingId === listingId && slot.controls.canDeactivate;

const isVisibleBlockedSlot = (slot: RegionalHighlightSlot): boolean =>
  slot.listingMatch?.assignable === false &&
  slot.listingMatch.reason !== RegionalHighlightListingMatchReason.DISTRICT_MISMATCH &&
  slot.listingMatch.reason !== RegionalHighlightListingMatchReason.LISTING_INELIGIBLE;

/**
 * Maps the listing-aware slots response into the single RH promotion state rendered by UBM.
 * Backend eligibility and `listingMatch` remain authoritative; the client only prioritizes a
 * free assignment over a replacement when both valid actions are available.
 */
export const getRegionalHighlightBookingState = (
  response: RegionalHighlightSlotsResponse | undefined,
  listingId: string,
): RegionalHighlightBookingState => {
  if (!response) {
    return { kind: 'hidden' };
  }

  const activeSlot = response.slots.find((slot) => isActiveForListing(slot, listingId));
  if (activeSlot) {
    return { kind: 'active', slot: activeSlot };
  }

  if (!response.listing || !response.listing.eligible || response.listing.listingId !== listingId) {
    return { kind: 'hidden' };
  }

  const assignableSlots = response.slots.filter((slot) => slot.listingMatch?.assignable === true);
  const selectedSlot =
    assignableSlots.find(
      (slot) => slot.listingMatch?.action === RegionalHighlightListingMatchAction.ASSIGN,
    ) ?? assignableSlots[0];
  if (selectedSlot) {
    return { kind: 'assignable', slot: selectedSlot };
  }

  const blockedSlot = response.slots.find(isVisibleBlockedSlot);
  if (blockedSlot) {
    return { kind: 'blocked', slot: blockedSlot };
  }

  return { kind: 'no-usable-slot' };
};
